import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { AlertCircle, ArrowRight, Loader2, PhoneCall, PhoneForwarded } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import { NOTIFY_EMAIL_MAX, notifyEmailError } from '@/lib/callbackTicket';
import { RequestDeadlineError, withDeadline } from '@/lib/requestDeadline';
import { ApiError } from '@/services/apiService';
import voiceService, { type CallbackTicketSettings, type VoiceSettings } from '@/services/voiceService';
import { TRANSFER_NUMBERS_ANCHOR } from '@/components/SalonTransferNumbersSection';

interface CallbackTicketSettingsCardProps {
  /** Selected agent number — only for the „gilt für alle Agenten“ hint: the setting is tenant-wide. */
  agentNumber: string | null;
  /** An address typed but not saved before the tab was left (null = none); shown instead of the stored one. */
  draftEmail?: string | null;
  /** Every edit of the address: the unsaved text, or null once it matches what is stored. */
  onDraftEmailChange?: (draft: string | null) => void;
}

type LoadState = 'loading' | 'ready' | 'unsupported' | 'error';

const errorText = (err: unknown, fallback: string) => (err instanceof Error && err.message ? err.message : fallback);

/** The block the server stored — or an error when it did not return one. */
function callbackTicketOf(response: VoiceSettings): CallbackTicketSettings {
  if (!response?.success) throw new Error(response?.error || 'Speichern fehlgeschlagen.');
  if (!response.callback_ticket) throw new Error('Der Server kennt diese Einstellung noch nicht.');
  return response.callback_ticket;
}

/**
 * „Rückruf-Ticket anbieten“ in the phone tab of the agent page: the switch
 * saves right away (optimistic, rolled back on error, like the handoff card);
 * the reception's e-mail address has its own „Speichern“. Tenant-wide — the
 * backend writes the company value also when an agent number is selected.
 */
export function CallbackTicketSettingsCard({
  agentNumber,
  draftEmail = null,
  onDraftEmailChange,
}: CallbackTicketSettingsCardProps) {
  const baseId = useId();
  const [state, setState] = useState<LoadState>('loading');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saved, setSaved] = useState<CallbackTicketSettings | null>(null);
  const [email, setEmail] = useState('');
  /** The field's text right now — an answer arriving later must not overwrite newer typing. */
  const emailRef = useRef('');
  /** Only the draft at mount counts: afterwards the field itself is the truth. */
  const initialDraftRef = useRef(draftEmail);
  const onDraftRef = useRef(onDraftEmailChange);
  onDraftRef.current = onDraftEmailChange;
  /** The server refused the address (422) — shown at the field until it changes. */
  const [serverEmailError, setServerEmailError] = useState<string | null>(null);
  const [toggling, setToggling] = useState(false);
  const [saving, setSaving] = useState(false);
  const [attempt, setAttempt] = useState(0);

  const load = useCallback(() => setAttempt((n) => n + 1), []);

  useEffect(() => {
    let cancelled = false;
    setState('loading');
    (async () => {
      try {
        const settings = await withDeadline(voiceService.getSettings(null));
        if (cancelled) return;
        if (!settings?.success) throw new Error(settings?.error || 'Einstellungen konnten nicht geladen werden.');
        if (!settings.callback_ticket) {
          setState('unsupported');
          return;
        }
        setSaved(settings.callback_ticket);
        const shown = initialDraftRef.current ?? settings.callback_ticket.notify_email ?? '';
        emailRef.current = shown;
        setEmail(shown);
        setLoadError(null);
        setState('ready');
      } catch (err) {
        if (cancelled) return;
        setLoadError(errorText(err, 'Einstellungen konnten nicht geladen werden.'));
        setState('error');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [attempt]);

  /**
   * After a timeout the server may have saved it after all: take its state
   * instead of trusting the rollback. Leaves the typed address alone.
   */
  const resync = async () => {
    try {
      const settings = await withDeadline(voiceService.getSettings(null));
      if (settings?.success && settings.callback_ticket) setSaved(settings.callback_ticket);
    } catch {
      // Still unreachable: the rolled-back state stays until the next visit.
    }
  };

  const busy = toggling || saving;
  const available = saved?.available !== false;
  const enabled = saved?.enabled === true;
  const emailError = notifyEmailError(email) ?? serverEmailError;
  const dirty = Boolean(saved) && email.trim() !== (saved?.notify_email ?? '');
  const canSave = dirty && !emailError && !busy;

  const toggleEnabled = async (next: boolean) => {
    if (!saved || busy || !available) return;
    const previous = saved;
    setSaved({ ...saved, enabled: next });
    setToggling(true);
    try {
      // Only the switch: the address stays as stored.
      const stored = callbackTicketOf(await withDeadline(voiceService.updateSettings({ callback_ticket: { enabled: next } })));
      setSaved(stored);
      toast.success(stored.enabled ? 'Rückruf-Ticket aktiviert.' : 'Rückruf-Ticket deaktiviert.');
    } catch (err) {
      setSaved(previous);
      toast.error(errorText(err, 'Konnte Einstellung nicht speichern.'));
      if (err instanceof RequestDeadlineError) void resync();
    } finally {
      setToggling(false);
    }
  };

  const saveEmail = async () => {
    if (!saved || !canSave) return;
    const value = email.trim();
    setSaving(true);
    try {
      const stored = callbackTicketOf(
        // "" removes the address on the server.
        await withDeadline(voiceService.updateSettings({ callback_ticket: { notify_email: value } })),
      );
      setSaved(stored);
      // Typed on while saving: that newer text stays (and stays unsaved).
      if (emailRef.current.trim() === value) {
        emailRef.current = stored.notify_email ?? '';
        setEmail(emailRef.current);
        onDraftRef.current?.(null);
      }
      toast.success(stored.notify_email ? 'E-Mail-Benachrichtigung gespeichert.' : 'E-Mail-Benachrichtigung entfernt.');
    } catch (err) {
      if (err instanceof ApiError && err.status === 422) {
        setServerEmailError('Diese Adresse nimmt der Server nicht an. Bitte prüfen.');
      } else {
        toast.error(errorText(err, 'Speichern fehlgeschlagen.'));
        if (err instanceof RequestDeadlineError) void resync();
      }
    } finally {
      setSaving(false);
    }
  };

  const onEmailChange = (value: string) => {
    emailRef.current = value;
    setEmail(value);
    setServerEmailError(null);
    onDraftRef.current?.(value.trim() === (saved?.notify_email ?? '') ? null : value);
  };

  const switchId = `${baseId}-enabled`;
  const emailId = `${baseId}-email`;
  const emailHintId = `${baseId}-email-hint`;
  const emailErrorId = `${baseId}-email-error`;
  const killHintId = `${baseId}-kill-switch`;
  const switchLabel = !available ? 'Abgeschaltet' : enabled ? 'Aktiv' : 'Aus';

  return (
    <Card className="overflow-hidden rounded-[28px] border-border shadow-sm">
      <CardHeader className="space-y-2 p-4 sm:p-6">
        <div className="flex items-start justify-between gap-4">
          <CardTitle className="flex items-center gap-2 pt-1 text-base">
            <PhoneCall className="h-4 w-4 shrink-0 text-brand" aria-hidden="true" />
            Rückruf-Ticket anbieten
          </CardTitle>
          {state === 'ready' && (
            <div className="flex shrink-0 items-center gap-2 pt-0.5">
              <Switch
                id={switchId}
                // Kill switch: nothing is offered, whatever the salon stored.
                checked={enabled && available}
                onCheckedChange={toggleEnabled}
                // Kill switch: really disabled. While saving: aria-disabled, so focus stays.
                disabled={!available}
                aria-disabled={busy || !available}
                aria-busy={toggling}
                aria-label="Rückruf-Ticket anbieten"
                aria-describedby={!available ? killHintId : undefined}
                className="data-[state=checked]:bg-brand aria-disabled:cursor-wait aria-disabled:opacity-60 disabled:cursor-not-allowed"
              />
              <Label htmlFor={switchId} className="min-w-[3rem] text-sm font-normal">
                {switchLabel}
              </Label>
            </div>
          )}
        </div>
        <p className="max-w-prose text-sm text-muted-foreground">
          Wenn eine Weiterleitung an den Salon nicht klappt, bietet der Agent an, einen Rückrufwunsch für euer Team zu
          hinterlegen.
        </p>
        {state === 'ready' && !available && (
          <p id={killHintId} className="flex items-center gap-1.5 text-xs font-medium text-warning-strong">
            <AlertCircle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            Vorübergehend für alle Salons abgeschaltet.
          </p>
        )}
        {agentNumber && state === 'ready' && (
          <p className="text-xs text-muted-foreground">
            Gilt für das ganze Unternehmen – für alle Agenten gleich.
          </p>
        )}
      </CardHeader>

      <CardContent className="space-y-4 p-4 pt-0 sm:p-6 sm:pt-0">
        {state === 'loading' && (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            Lädt…
          </p>
        )}

        {state === 'unsupported' && (
          <p className="rounded-lg bg-muted px-3 py-2 text-sm text-muted-foreground">
            Diese Funktion wird gerade eingeführt und ist für dein Konto noch nicht verfügbar.
          </p>
        )}

        {state === 'error' && (
          <div className="flex flex-wrap items-center gap-3 rounded-lg border border-destructive/40 px-3 py-2 text-sm">
            <AlertCircle className="h-4 w-4 text-destructive-strong" aria-hidden="true" />
            <span className="flex-1">{loadError}</span>
            <Button variant="outline" size="sm" onClick={load}>
              Erneut laden
            </Button>
          </div>
        )}

        {state === 'ready' && saved && (
          <>
            <p className="text-xs leading-5 text-muted-foreground">
              Rückrufwünsche erscheinen in der Glocke und in „Verläufe“ oben unter „Wartet auf Rückmeldung“, bis
              jemand sie als erledigt markiert.
              {!enabled && ' Die E-Mail-Adresse unten gilt, sobald das Rückruf-Ticket eingeschaltet ist.'}
            </p>

            <div className="space-y-2 rounded-xl border border-border px-4 py-3">
              <Label htmlFor={emailId} className="text-sm font-medium">
                Benachrichtigung an (E-Mail)
              </Label>
              <p id={emailHintId} className="text-xs leading-5 text-muted-foreground">
                Optional: Jeder neue Rückrufwunsch geht zusätzlich als E-Mail an diese Adresse. Leer lassen = keine
                E-Mail.
              </p>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
                <Input
                  id={emailId}
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  value={email}
                  maxLength={NOTIFY_EMAIL_MAX}
                  placeholder="empfang@salon.de"
                  aria-invalid={Boolean(emailError)}
                  aria-describedby={emailError ? `${emailHintId} ${emailErrorId}` : emailHintId}
                  onChange={(e) => onEmailChange(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      void saveEmail();
                    }
                  }}
                  className={cn('bg-background sm:flex-1', emailError && 'border-destructive-strong')}
                />
                <Button
                  onClick={saveEmail}
                  aria-disabled={!canSave}
                  aria-busy={saving}
                  className="min-w-[140px] bg-brand text-brand-foreground hover:bg-brand-hover aria-disabled:cursor-not-allowed aria-disabled:opacity-50 aria-disabled:hover:bg-brand"
                >
                  {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
                  Speichern
                </Button>
              </div>
              {/* Always mounted: a live region only announces changes inside an existing one. */}
              <p
                id={emailErrorId}
                data-testid="notify-email-error"
                aria-live="polite"
                className={emailError ? 'text-xs font-medium text-destructive-strong' : 'sr-only'}
              >
                {emailError ?? ''}
              </p>
            </div>

            <div className="flex flex-col gap-2 rounded-xl bg-muted/60 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="flex items-start gap-2 text-xs leading-5 text-muted-foreground">
                <PhoneForwarded className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                Damit die Weiterleitung klappt, braucht der Agent eine direkte Durchwahl, die an eurer Rezeption
                klingelt.
              </p>
              <Link
                // Profil › Unternehmensdaten: the direct number (SalonTransferNumbersSection).
                to={`/profile#${TRANSFER_NUMBERS_ANCHOR}`}
                className="inline-flex shrink-0 items-center gap-1 self-start rounded text-xs font-semibold text-brand underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/60 sm:self-center"
              >
                Durchwahl einrichten
                <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
              </Link>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default CallbackTicketSettingsCard;
